/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { inject, observer } from 'mobx-react';
import constants from '../../constants';
import {
  getTaxonomies,
  getTaxonomiesTypes,
  deleteTaxonomy,
  createNewTaxonomy,
  updateTaxonomy
} from '../../services/getTaxonomiesService';
import ContentMetadataEditBox from './ContentMetadataEditBox';
import {
  CustomDialog,
  DialogHeader,
  DialogContent,
  DialogActions
} from '../../components/common/Dialog';
import './ContentMetadata.style.scss';
import { formatTaxonomyText } from '../../utils/utils';

const EDIT_BOX_TYPES = {
  ADD_NEW_TAXONOMY: 'AddNewTaxonomy',
  EDIT_TAXONOMY: 'EditTaxonomy'
};

const ContentMetadata = props => {
  const {
    store: {
      LineOfBusiness: { getLineOfBusinessList, lobList }
    }
  } = props;

  const { SELECT_LOB, TAXONOMY_TYPE_HEADER, NO_TAXONOMY_TYPES, NO_TAXONOMIES } =
    constants;

  const [selectedLob, setSelectedLob] = useState('');
  const [taxonomiesTypes, setTaxonomiesTypes] = useState([]);
  const [taxonomies, setTaxonomies] = useState([]);
  const [selectedTaxonomyType, setSelectedTaxonomyType] = useState({
    taxonomyType: '--',
    context: ''
  });
  const [selectedTaxonomy, setSelectedTaxonomy] = useState({});
  const [openEditBox, setOpenEditBox] = useState(null);
  const [editedTaxonomy, setEditedTaxonomy] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [segmentToDelete, setSegmentToDelete] = useState(null);
  const [editTaxonomyValue, setEditTaxonomyValue] = useState('');
  const [editTaxonomyLabel, setEditTaxonomyLabel] = useState('');

  const resetStates = () => {
    setTaxonomies([]);
    setSelectedTaxonomyType({ taxonomyType: '--', context: '' });
    setSelectedTaxonomy({});
    setTaxonomiesTypes([]);
    setSelectedLob('');
    setOpenEditBox(null);
    setEditedTaxonomy('');
    setEditTaxonomyValue('');
    setEditTaxonomyLabel('');
  };

  useEffect(() => {
    getLineOfBusinessList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeTaxonomyType = async taxonomyType => {
    const response = await getTaxonomies(
      selectedLob,
      taxonomyType.taxonomyType,
      true
    );
    setTaxonomies(response);
    setSelectedTaxonomyType(taxonomyType);
    setEditedTaxonomy('');
  };

  const handleLobChange = async event => {
    const { value: lob } = event.target;
    resetStates();

    // fetch taxonomyTypes for this lob
    const response = await getTaxonomiesTypes(lob);
    const formattedTaxonomyTypes = [];
    Object.keys(response).forEach(context => {
      response[context].forEach(taxonomyType => {
        if (taxonomyType !== 'subsegment') {
          formattedTaxonomyTypes.push({
            taxonomyType,
            context
          });
        }
      });
    });
    setTaxonomiesTypes(formattedTaxonomyTypes);
    setSelectedLob(lob);
  };

  // to populate taxonomies whenever lob is changed
  useEffect(() => {
    if (taxonomiesTypes.length > 0) {
      changeTaxonomyType(taxonomiesTypes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLob]);

  const closeEditBox = () => {
    setOpenEditBox(null);
    setEditTaxonomyValue('');
    setEditTaxonomyLabel('');
  };

  const handleAddTaxonomy = async (taxonomyLabel, subsegments = []) => {
    const { taxonomyType, context } = selectedTaxonomyType;
    const response = await createNewTaxonomy(
      selectedLob,
      taxonomyType,
      context,
      taxonomyLabel,
      subsegments // Pass subsegments to the service if supported
    );
    const newTaxonomy = {
      value: response.value,
      label: response.label,
      subsegment: response.subSegment || subsegments // Use returned or input subsegments
    };
    setTaxonomies(existingTaxonomies => [newTaxonomy, ...existingTaxonomies]);
    setEditedTaxonomy(newTaxonomy.value);
  };

  const handleEditTaxonomy = async (taxonomyLabel, subsegments = []) => {
    const { taxonomyType, context } = selectedTaxonomyType;

    // If subsegments is empty, get current subsegments from selectedTaxonomy
    let finalSubsegments = subsegments;
    if (
      (!subsegments || subsegments.length === 0) &&
      selectedTaxonomy &&
      selectedTaxonomy.subsegment
    ) {
      finalSubsegments = selectedTaxonomy.subsegment;
    }

    const payload = {
      label: taxonomyLabel,
      value: selectedTaxonomy.value,
      subsegments: finalSubsegments
    };
    const response = await updateTaxonomy(
      selectedLob,
      taxonomyType,
      context,
      payload,
      finalSubsegments
    );
    const updatedTaxonomy = {
      value: response.value,
      label: response.label,
      subsegment: response.subSegment || finalSubsegments // Use returned or input subsegments
    };

    // removing existing taxonomy and adding edited taxonomy on top of the list
    setTaxonomies(existingTaxonomies => [
      updatedTaxonomy,
      ...existingTaxonomies.filter(
        item => item.value !== selectedTaxonomy.value
      )
    ]);
    setEditedTaxonomy(updatedTaxonomy.value);
  };

  const handleOpenEditBox = taxonomy => {
    setEditTaxonomyValue(taxonomy.value);
    setEditTaxonomyLabel(taxonomy.label);
    setSelectedTaxonomy(taxonomy);
    setEditedTaxonomy('');
    setOpenEditBox(EDIT_BOX_TYPES.EDIT_TAXONOMY);
  };

  const handleOpenAddNewBox = () => {
    setEditTaxonomyValue('');
    setEditTaxonomyLabel('');
    setEditedTaxonomy('');
    setOpenEditBox(EDIT_BOX_TYPES.ADD_NEW_TAXONOMY);
  };

  const handleOpenDeleteDialog = taxonomy => {
    setSegmentToDelete(taxonomy);
    setDeleteDialogOpen(true);
  };

  const handleDeleteTaxonomy = async taxonomy => {
    const { taxonomyType, context } = selectedTaxonomyType;
    // Call the service to delete the taxonomy
    await deleteTaxonomy(selectedLob, taxonomyType, context, taxonomy);
    // Remove the deleted taxonomy from the local state
    setTaxonomies(existingTaxonomies =>
      existingTaxonomies.filter(item => item.value !== taxonomy.value)
    );
  };

  const handleConfirmDeleteSegment = async () => {
    if (segmentToDelete) {
      await handleDeleteTaxonomy(segmentToDelete);
    }
    setDeleteDialogOpen(false);
    setSegmentToDelete(null);
  };

  const handleCancelDeleteSegment = () => {
    setDeleteDialogOpen(false);
    setSegmentToDelete(null);
  };

  // text for delete taxonomy message
  const taxonomyValue = selectedTaxonomyType.taxonomyType;
  const DeleteTaxonomyText =
    constants.TAXONOMY_DELETE +
    ' ' +
    (segmentToDelete ? segmentToDelete.label : '') +
    ' ' +
    taxonomyValue +
    `?`;
  return (
    <div className="project-content-metadata-wrapper">
      <div className="lob-selection-wrapper">
        <TextField
          select
          inputProps={{
            'data-testid': 'lob-input',
            'aria-label': 'lob-input'
          }}
          size="medium"
          label={SELECT_LOB}
          value={selectedLob || ''}
          onChange={handleLobChange}
          className=""
          margin="dense"
          SelectProps={{
            SelectDisplayProps: {
              'data-testid': 'lob-dropdown'
            },
            MenuProps: {
              container: this,
              className: 'lob-dropdown-menu'
            }
          }}
        >
          {lobList &&
            lobList.map(opt => (
              <MenuItem key={opt.label} value={opt.aliasToLOB} name={opt.label}>
                {opt.label}
              </MenuItem>
            ))}
        </TextField>
      </div>
      <div className="taxonomies-list-wrapper">
        <div className="list-container taxonomy-types-list">
          <div className="list-header">{TAXONOMY_TYPE_HEADER}</div>
          <List className="list-content">
            {taxonomiesTypes.length === 0 && selectedLob && (
              <div className="no-rules">{NO_TAXONOMY_TYPES}</div>
            )}
            {taxonomiesTypes.map(data => {
              return (
                <ListItem
                  key={data.taxonomyType}
                  className={
                    selectedTaxonomyType.taxonomyType === data.taxonomyType
                      ? 'taxonomy-type-item selected-taxonomy-type'
                      : 'taxonomy-type-item'
                  }
                >
                  <ListItemText
                    primary={`${data.taxonomyType} (${data.context})`}
                    onClick={() => changeTaxonomyType(data)}
                  />
                  <ListItemSecondaryAction>
                    <ArrowForwardIosIcon fontSize="small" />
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        </div>
        <div className="list-container taxonomies-value-list">
          <div className="list-header">
            <div className="list-header-selected-taxonomy">
              {formatTaxonomyText(selectedTaxonomyType.taxonomyType)}
            </div>
            {taxonomiesTypes.length > 0 && (
              <Button
                className="add-taxonomy-button"
                onClick={handleOpenAddNewBox}
                aria-label="Add taxonomy"
                size="large"
              >
                <div className="add-segment">
                  <AddIcon />
                  <Typography>
                    Add {selectedTaxonomyType.taxonomyType}
                  </Typography>
                </div>
              </Button>
            )}
          </div>
          <List className="list-content">
            {taxonomies.length === 0 &&
              selectedTaxonomyType &&
              selectedTaxonomyType.taxonomyType !== '--' && (
                <div className="no-rules">{NO_TAXONOMIES}</div>
              )}
            {taxonomies.map(data => {
              return (
                <ListItem
                  key={data.value}
                  autoFocus={editedTaxonomy === data.value}
                  className="taxonomy-item"
                >
                  <div className="list-item-text-wrapper">
                    <div className="list-item-text">
                      <ListItemText primary={data.label} />
                      <IconButton
                        onClick={() => handleOpenEditBox(data)}
                        aria-label={`Edit ${data.label}`}
                        size="large"
                        className="edit-icon-btn"
                      >
                        <EditIcon color="primary" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleOpenDeleteDialog(data)}
                        aria-label={`Delete ${data.label}`}
                        size="large"
                        className="delete-icon-btn"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                    {data.subsegment && data.subsegment.length > 0 && (
                      <>
                        <List className="subsegment-list">
                          {data.subsegment.map(sub => (
                            <ListItem
                              className="subsegment-item"
                              key={sub.label}
                            >
                              <ListItemText
                                primary={`${sub.label}`}
                                className="subsegment-list-text"
                              />
                            </ListItem>
                          ))}
                        </List>
                        <div className="list-divider">
                          <Divider className="divider" />
                        </div>
                      </>
                    )}
                  </div>
                </ListItem>
              );
            })}
          </List>
        </div>
      </div>
      <ContentMetadataEditBox
        open={!!openEditBox}
        isAddNewBox={openEditBox === EDIT_BOX_TYPES.ADD_NEW_TAXONOMY}
        handleAddTaxonomy={handleAddTaxonomy}
        handleEditTaxonomy={handleEditTaxonomy}
        currentValue={editTaxonomyValue}
        currentLabel={editTaxonomyLabel}
        closeEditBox={closeEditBox}
        selectedTaxonomyType={selectedTaxonomyType.taxonomyType}
        taxonomies={taxonomies}
      />
      {/* Segment Delete Confirmation Dialog */}
      <CustomDialog
        open={deleteDialogOpen}
        onClose={handleCancelDeleteSegment}
        className="segment-delete-dialog"
      >
        <DialogHeader
          onClose={handleCancelDeleteSegment}
          title={
            <span className="segment-delete-dialog-header">
              <ErrorOutlineIcon className="segment-delete-dialog-header-icon" />
              Delete {formatTaxonomyText(taxonomyValue)}
            </span>
          }
        />
        <DialogContent>
          <Typography className="segment-delete-dialog-text">
            {DeleteTaxonomyText}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelDeleteSegment}
            color="primary"
            variant="outlined"
            className="taxonomy-action-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteSegment}
            color="error"
            variant="contained"
            className="taxonomy-action-button"
            startIcon={<DeleteOutlineOutlinedIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </CustomDialog>
    </div>
  );
};

ContentMetadata.propTypes = {
  store: PropTypes.shape({
    LineOfBusiness: PropTypes.shape({
      getLineOfBusinessList: PropTypes.func.isRequired,
      lobList: PropTypes.arrayOf(
        PropTypes.shape({
          lineOfBussiness: PropTypes.string
        })
      )
    })
  })
};

export default inject('store')(observer(ContentMetadata));
